import React from "react";
import { Button, ButtonProps } from "react-bootstrap";
import { withTooltip } from "../../../hoc/withTooltip";
import { Spinner } from "../../../shared/Spinner";
import { sendGAEvent } from "../../../utils/utils";

type Props = {
  onClick?: (...args: any[]) => any;
  action: string;
  category?: string;
  label?: string;
  variant?: string;
  className?: string;
  style?: any;
  id?: string;
  target?: string;
};

const TrackedButton: React.FC<Props & ButtonProps> = ({
  children,
  onClick,
  action,
  category,
  label,
  ...rest
}) => {
  const trackedOnClick = () => {
    sendGAEvent({ action, category, label });
    if (onClick) {
      onClick();
    }
  };
  return (
    <Button {...rest} onClick={trackedOnClick}>
      {children}
    </Button>
  );
};

TrackedButton.defaultProps = {
  category: "other",
  label: "",
};

// Solid bootstrap variants get a white spinner so it's visible on the colored fill.
// Outline/link/light variants get the default spinner color so it remains visible.
const SOLID_VARIANTS = new Set([
  "primary",
  "secondary",
  "success",
  "danger",
  "warning",
  "info",
  "dark",
]);

const spinnerColorFor = (variant?: string) =>
  variant && SOLID_VARIANTS.has(variant) ? "#fff" : undefined;

type WithLoading = { isLoading?: boolean };

export const LoadingButton: React.FC<ButtonProps & WithLoading> = ({
  isLoading,
  disabled,
  children,
  variant,
  ...rest
}) => (
  <Button {...rest} variant={variant} disabled={disabled || isLoading}>
    {isLoading && (
      <span style={{ marginRight: "0.5rem", verticalAlign: "middle" }}>
        <Spinner size="sm" color={spinnerColorFor(variant)} />
      </span>
    )}
    {children}
  </Button>
);

export const LoadingTrackedButton: React.FC<
  Props & ButtonProps & WithLoading
> = ({ isLoading, disabled, children, variant, ...rest }) => (
  <TrackedButton
    {...(rest as Props & ButtonProps)}
    variant={variant}
    disabled={disabled || isLoading}
  >
    {isLoading && (
      <span style={{ marginRight: "0.5rem", verticalAlign: "middle" }}>
        <Spinner size="sm" color={spinnerColorFor(variant)} />
      </span>
    )}
    {children}
  </TrackedButton>
);

export const ButtonWithTooltip = withTooltip(Button);
export const TrackedButtonWithTooltip = withTooltip(TrackedButton);

export default TrackedButton;
